import { speak, sendBeacon } from './api.js';
import type { Settings } from './settings.js';
import type { WordTiming } from './karaoke.js';
import type { PageContext } from './pageContext.js';

export type PlayerConfig = {
  apiBase: string;
  apiKey: string;
  getSettings: () => Settings;
  // Returns the page-context object to attach to /v1/speak, or null when
  // the toggle is off. Resolved per-call so the gate can flip live.
  getPageContext: () => PageContext | null;
  onStateChange: (state: 'idle' | 'loading' | 'playing') => void;
  // words      = timed boundaries from the engine.
  // range      = the user's original selection at request time. The
  //              in-page highlighter wraps text inside this range when
  //              the audio language matches the page text.
  // translated = the post-translation text the audio is actually reading,
  //              when the backend translated upstream. Set when the page
  //              selection language differs from the audio language; the
  //              caller renders this text in a bubble + runs karaoke
  //              inside the bubble instead of in-page (in-page would
  //              try to map e.g. Welsh audio words onto English page
  //              words and light up the wrong ones).
  // All three are null when audio is stopping or timings aren't available.
  onWords: (words: WordTiming[] | null, range: Range | null, translated: string | null) => void;
  onTimeUpdate: (currentTimeS: number) => void;
  onToast: (message: string, kind?: 'info' | 'error') => void;
};

type CompactWord = { w: string; t0: number; t1: number; k: 'w' | 'p' };

function decodeWords(header: string | null): WordTiming[] | null {
  if (!header) return null;
  try {
    const json = atob(header);
    const arr = JSON.parse(json) as CompactWord[];
    if (!Array.isArray(arr)) return null;
    return arr.map((w) => ({ word: w.w, t0: w.t0, t1: w.t1, kind: w.k === 'p' ? 'punctuation' : 'word' }));
  } catch {
    return null;
  }
}

// Decode a base64 header carrying UTF-8 text. atob() returns a binary
// string, so a naive `atob(h)` mangles non-ASCII (Welsh diacritics like
// ŵ ŷ â ô would come back as multi-byte garbage). Round-trip through
// TextDecoder('utf-8') restores them. Returns null on malformed input
// rather than throwing so the caller can fall through cleanly.
function decodeBase64Utf8(header: string | null): string | null {
  if (!header) return null;
  try {
    const bin = atob(header);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return null;
  }
}

const MSE_MPEG_SUPPORTED =
  typeof window !== 'undefined' &&
  typeof window.MediaSource !== 'undefined' &&
  window.MediaSource.isTypeSupported('audio/mpeg');

/**
 * Headless audio player. No UI — just owns an <audio> element and the
 * lifecycle around fetching + decoding TTS audio. The pill is the only
 * visible surface; it reflects state via Player.onStateChange callbacks.
 *
 * When the backend returns a streamed MP3 response (x-llais-streamed: 1
 * + content-type: audio/mpeg, currently only OpenAI tts-1) and MSE
 * supports audio/mpeg in the host browser, we feed chunks straight into
 * a SourceBuffer so playback can start mid-synthesis. For everything
 * else — Piper WAV, cached responses, browsers without MSE support —
 * we fall back to a blob URL after awaiting the full body.
 */
export class Player {
  private audio: HTMLAudioElement;
  private currentUrl: string | null = null;
  private abort: AbortController | null = null;
  private pendingBeacon: string | null = null;
  private requestStartedAt = 0;

  constructor(private cfg: PlayerConfig) {
    this.audio = document.createElement('audio');
    this.audio.preload = 'auto';

    this.audio.addEventListener('play', () => this.cfg.onStateChange('playing'));
    this.audio.addEventListener('pause', () => {
      if (!this.audio.ended) this.cfg.onStateChange('idle');
    });
    this.audio.addEventListener('ended', () => {
      this.cfg.onStateChange('idle');
      this.cfg.onWords(null, null, null);
      this.flushBeacon();
    });
    this.audio.addEventListener('error', () => {
      this.cfg.onStateChange('idle');
      this.cfg.onWords(null, null, null);
    });
    this.audio.addEventListener('timeupdate', () => {
      this.cfg.onTimeUpdate(this.audio.currentTime);
    });
  }

  get element(): HTMLAudioElement {
    return this.audio;
  }

  async load(text: string, range: Range | null = null): Promise<void> {
    this.abort?.abort();
    this.abort = new AbortController();
    const localAbort = this.abort;
    this.pendingBeacon = null;
    this.requestStartedAt = Date.now();
    this.cfg.onStateChange('loading');

    const s = this.cfg.getSettings();

    // Prefer MP3 when MSE can decode it — that's the path that lets the
    // backend stream chunks straight through and the audio element starts
    // playing mid-synthesis. If MSE isn't around, opus is smaller for the
    // blob fallback path.
    const format: 'mp3' | 'opus' = MSE_MPEG_SUPPORTED ? 'mp3' : 'opus';

    const pageContext = this.cfg.getPageContext();

    const result = await speak({
      apiBase: this.cfg.apiBase,
      apiKey: this.cfg.apiKey,
      text,
      voice: s.voice,
      speed: s.speed,
      translate: s.translate,
      translateTo: s.translateTo as 'cy' | 'en',
      format,
      ...(pageContext ? { pageContext } : {}),
      signal: localAbort.signal,
    });

    if (localAbort.signal.aborted) return;

    if (result.kind === 'audio') {
      this.pendingBeacon = result.beacon;
      const words = decodeWords(result.response.headers.get('x-llais-words'));
      const translatedText = decodeBase64Utf8(result.response.headers.get('x-llais-translated-text'));
      // Karaoke gate: words available + setting enabled. When translation
      // happened the caller renders translatedText in the bubble; when
      // it didn't, the caller wraps text inside the page range. Pass
      // both signals through so the caller can pick the right surface.
      const wantKaraoke = !!(words && s.karaoke);
      this.cfg.onWords(
        wantKaraoke ? words : null,
        wantKaraoke ? range : null,
        wantKaraoke ? translatedText : null,
      );
      const streamed = result.response.headers.get('x-llais-streamed') === '1';
      const contentType = result.response.headers.get('content-type') ?? '';
      // Chrome throttles MediaSource attachment in background tabs (sourceopen
      // never fires) — skip MSE entirely if the document is hidden. The
      // blob path still works in hidden tabs because it doesn't go through
      // the source-attachment machinery.
      const canStream =
        streamed &&
        contentType.startsWith('audio/mpeg') &&
        MSE_MPEG_SUPPORTED &&
        !document.hidden &&
        !!result.response.body;

      try {
        let played = false;
        if (canStream && result.response.body) {
          played = await this.playMseStream(result.response.body, s.speed, localAbort.signal);
        }
        if (!played) {
          // MSE path didn't apply or bailed out (hidden tab / sourceopen
          // timeout). Fall back to the original blob path. If the MSE
          // attempt already consumed part of the body, response.blob()
          // will reject — that's fine, we surface as idle.
          const blob = await result.response.blob();
          if (localAbort.signal.aborted) return;
          this.swapUrl(URL.createObjectURL(blob));
          this.audio.playbackRate = s.speed;
          await this.audio.play();
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        this.cfg.onStateChange('idle');
      }
    } else if (result.kind === 'not_implemented') {
      this.cfg.onToast('Backend stub — synthesis not wired up yet.', 'info');
      this.cfg.onStateChange('idle');
    } else {
      if (result.status !== 0) {
        this.cfg.onToast(`Error: ${result.message}`, 'error');
      }
      this.cfg.onStateChange('idle');
    }
  }

  stop(): void {
    this.abort?.abort();
    this.audio.pause();
    this.swapUrl(null);
    this.cfg.onStateChange('idle');
  }

  setSpeed(speed: number): void {
    this.audio.playbackRate = speed;
  }

  /**
   * Pipe a streaming MP3 response into a MediaSource so playback starts
   * the moment the first chunk lands instead of after the whole synthesis
   * completes. The SourceBuffer's appendBuffer call is exclusive (throws
   * INVALID_STATE if it fires while 'updating' is true), so chunks are
   * serialised through waitForIdle().
   *
   * Returns `true` if MSE handled the playback, `false` if we should fall
   * back to the blob path (e.g. hidden tab where MediaSource never opens —
   * Chrome throttles source-attachment in background tabs, and the widget
   * shouldn't hang on tabs that aren't currently visible).
   */
  private async playMseStream(
    body: ReadableStream<Uint8Array>,
    speed: number,
    signal: AbortSignal,
  ): Promise<boolean> {
    const ms = new MediaSource();
    this.swapUrl(URL.createObjectURL(ms));
    this.audio.playbackRate = speed;

    const opened = await Promise.race<'open' | 'timeout' | 'aborted'>([
      new Promise((resolve) => {
        if (ms.readyState === 'open') return resolve('open');
        ms.addEventListener('sourceopen', () => resolve('open'), { once: true });
      }),
      new Promise((resolve) => setTimeout(() => resolve('timeout'), 2500)),
      new Promise((resolve) => {
        signal.addEventListener('abort', () => resolve('aborted'), { once: true });
      }),
    ]);

    if (opened === 'aborted') {
      this.endStreamIfOpen(ms);
      return true;
    }
    if (opened === 'timeout') {
      // Tab likely hidden / source-attachment stalled. Bail and let the
      // caller fall back to the blob path. Reset audio.src so the blob
      // assignment doesn't race with the still-attached MediaSource.
      this.endStreamIfOpen(ms);
      this.swapUrl(null);
      return false;
    }

    const sb = ms.addSourceBuffer('audio/mpeg');
    const waitForIdle = (): Promise<void> =>
      new Promise<void>((resolve) => {
        if (!sb.updating) return resolve();
        sb.addEventListener('updateend', () => resolve(), { once: true });
      });

    const reader = body.getReader();
    let firstChunkAppended = false;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (signal.aborted) {
          try { reader.cancel(); } catch { /* ignore */ }
          break;
        }
        const { done, value } = await reader.read();
        if (done) break;
        if (!value || value.length === 0) continue;
        await waitForIdle();
        if (signal.aborted) break;
        // TS5 widens Uint8Array.buffer to ArrayBufferLike (potentially
        // SharedArrayBuffer), which appendBuffer's BufferSource type
        // rejects. reader.read() on a fetch Response always yields
        // ArrayBuffer-backed views in practice; assert through BufferSource.
        sb.appendBuffer(value as BufferSource);
        if (!firstChunkAppended) {
          firstChunkAppended = true;
          // Kick off playback as soon as the first chunk is in. The audio
          // element happily plays while more chunks are still being appended.
          this.audio.play().catch(() => { /* autoplay may be blocked */ });
        }
      }
      await waitForIdle();
      this.endStreamIfOpen(ms);
      return true;
    } catch (err) {
      try { reader.cancel(); } catch { /* ignore */ }
      this.endStreamIfOpen(ms, 'decode');
      throw err;
    }
  }

  private endStreamIfOpen(ms: MediaSource, error?: 'network' | 'decode'): void {
    if (ms.readyState !== 'open') return;
    try {
      if (error) ms.endOfStream(error);
      else ms.endOfStream();
    } catch {
      /* ignore — sourcebuffer may still be updating, ended already */
    }
  }

  private flushBeacon(): void {
    if (!this.pendingBeacon) return;
    const token = this.pendingBeacon;
    this.pendingBeacon = null;
    void sendBeacon(this.cfg.apiBase, token, Date.now() - this.requestStartedAt);
  }

  private swapUrl(next: string | null): void {
    if (this.currentUrl) URL.revokeObjectURL(this.currentUrl);
    this.currentUrl = next;
    this.audio.src = next ?? '';
  }
}
